import { Meal, UpdateMealRequest } from "@/clients";
import { MealTime } from "@/clients/models/MealTime";
import { RightIcon } from "@/components/icons/right-icon";
import { LocalStorageKeys } from "@/constants/local-storage-keys";
import { QueryKeys } from "@/constants/query-keys";
import { useUpdatePlanMeal } from "@/hooks/requests/plan/use-update-plan-meal";
import { useMealDetailData } from "../../hooks/use-meal-detail-data";

import { useToast } from "@/hooks/ui/use-toast";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { useIonRouter } from "@ionic/react";
import { useQueryClient } from "@tanstack/react-query";
import { memo } from "react";

interface AlternativeMealItemProps {
  meal: Meal;
  mealTime: MealTime;
  mealDay: string;
  targetDate?: string;
  mealIndex?: number;
  currentMealId?: string;
  requestId?: string | null;
  onComplete?: () => void;
  onOpenMealDetail?: () => void;
  /** Position/index of this meal in the alternatives list */
  selectedPosition?: number;
  isExtra?: boolean;
}

export const AlternativeMealItem = memo(function AlternativeMealItem({
  meal,
  mealTime,
  mealDay,
  targetDate,
  mealIndex,
  currentMealId,
  requestId,
  onComplete,
  onOpenMealDetail,
  selectedPosition,
  isExtra = false,
}: AlternativeMealItemProps) {
  const queryClient = useQueryClient();
  const navigation = useIonRouter();
  const { showError, showInfo } = useToast();
  const { updateMealAsync, isLoading: isUpdating } = useUpdatePlanMeal();

  const handlePickMeal = async () => {
    if (isUpdating) {
      return;
    }
    if (!meal?.id || !mealTime || !mealDay) {
      return;
    }

    const updateMealRequest: UpdateMealRequest = {
      day: mealDay,
      mealIndex: mealIndex,
      currentMealId: currentMealId ?? undefined,
      mealTime: mealTime,
      meal_id: meal.id ?? "",
      meal_name: meal.name,
      requestId: requestId ?? undefined,
      selectedPosition: selectedPosition ?? undefined,
      targetDate: targetDate ?? undefined,
      isExtra: isExtra || undefined,
    };

    try {
      await updateMealAsync(updateMealRequest);
      onComplete?.();
      const searchParamsJson = localStorage.getItem(LocalStorageKeys.SWAP_PLAN_SEARCH_PARAMS);
      if (searchParamsJson) {
        const searchParams = JSON.parse(searchParamsJson);
        const newSearchParams = new URLSearchParams(searchParams);
        navigation.push(`/plans/my-plan?${newSearchParams.toString()}`, "none", "replace");
      } else {
        navigation.push("/plans/my-plan", "none", "replace");
      }
      localStorage.removeItem(LocalStorageKeys.SWAP_PLAN_MEAL_DATA);
      queryClient.removeQueries({
        queryKey: [QueryKeys.PLAN_ALTERNATIVE, meal.id, mealTime],
      });
      showInfo(`I’ve updated your ${mealTime}!`, {
        duration: 5000,
      });
    } catch (error) {
      if (error instanceof Error) {
        showError(error.message);
      } else {
        showError("An unknown error occurred: " + error);
      }
    }
  };

  // guard against broken svg as image to display default svg in app
  if (meal.imageUri.toLowerCase().endsWith(".svg")) {
    meal.imageUri = "";
  }

  // guard against bad image urls of shape https://.../fresh-recipes/https://...
  if (meal.imageUri.indexOf("fresh-recipes/http") !== -1) {
    console.log(meal.id);
    meal.imageUri = "";
  }

  const { onMealDetailClick } = useMealDetailData();

  return (
    <div
      role="button"
      key={meal.id}
      onClick={() => {
        onOpenMealDetail?.();
        onMealDetailClick(
          {
            meal,
            mealTime,
            mealIndex,
            mealDay,
            targetDate,
          },
          true,
        );
      }}
      className={cn("flex h-auto items-center rounded-2xl bg-white", {
        "cursor-not-allowed opacity-50": isUpdating,
      })}
    >
      <div className="border-li-icon flex h-auto flex-1 items-center justify-between border-b">
        <h4
          className={cn(textVariants.body, "mx-1 flex h-20 items-center justify-start text-left")}
        >
          <span className="line-clamp-3 w-full">{meal.name}</span>
        </h4>

        <div className="flex items-center justify-center gap-2">
          <div
            onClick={(event) => {
              handlePickMeal();
              event.stopPropagation();
            }}
            className={cn("text-os-brand", "")}
            aria-label="Choose"
          >
            Choose
          </div>
          <RightIcon className="h-4 w-4 text-[#9D9D9D]" />
        </div>
      </div>
    </div>
  );
});
